import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Privileges} from "../../../admin/privileges/privileges.constants";
import {AuthService} from "../../../../services/auth/auth.service";
import {ValidationService} from "../../../../services/validation/validation.service";
import {CustomerService} from "../../../../services/customer/customer.service";
import {EventService} from "../../../../services/event/event.service";
import {UserService} from "../../../../services/user/user.service";
import {SalespointsService} from "../../../../services/salespoints/salespoints.service";
import {CartService} from "../../../../services/cart/cart.service";
import moment from "moment/moment";
import {cloneDeep, find} from "lodash-es";
import {finalize} from "rxjs/operators";
import {forkJoin, Observable, ReplaySubject, Subscription} from "rxjs";
import {SELF_SERVICE_EXCLUDED_STATUSES, STATUS_DRAFT} from "../../event-search/event-search.constants";
import {
  Customer,
  EventLimited,
  EventStatus,
  EventType,
  PaymentTypeEnum,
  SalespointWithAlias,
  TicketReleaseType,
  TransactionType,
  UserCore
} from '../../../../generated/sales-terminal-client';
import {ScrollLoad} from '../../../../components/scroll-load-component/ScrollLoad';
import {DatePickerConfig} from "../../../../components/datepicker/datepicker.component";
import {DATEPICKER_PRESETS_MAPPED} from "../../../../components/datepicker/datepicker.constants";
import {UtilService} from "../../../../services/util/util.service";
import {NavigationEnd, Router} from "@angular/router";
import {TransactionFilter, TransactionsService} from '../../../../services/transactions/transactions.service';
import {TextInputType} from '../../../../components/forms/text-input/text-input.component';

@Component({
  selector: 'app-transactions-filters',
  templateUrl: './transactions-filters.component.html',
  styleUrls: ['./transactions-filters.component.scss']
})
export class TransactionsFiltersComponent implements OnInit, OnDestroy {
  protected readonly Privileges = Privileges;
  static MAX_DATE_RANGE = 31;
  private _filters: TransactionFilter;

  @Input()
  public set filters(value: TransactionFilter) {
    this._filters = value;
    this.filtersChange.emit(this._filters);
  }
  public get filters(): TransactionFilter {
    return this._filters;
  }
  @Output()
  public filtersChange: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public search: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public isLoading: boolean;

  @Input()
  public showMobileFilters: boolean;
  @Output()
  public showMobileFiltersChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  public hasResults: boolean;

  @Input()
  public legacyFilters: boolean;

  @Input()
  public noSearches: boolean;

  public updatingData: boolean;
  public salespoints: SalespointWithAlias[] = [];
  public salespointsFiltered: ReplaySubject<SalespointWithAlias[]> = new ReplaySubject<SalespointWithAlias[]>();
  public sellers: UserCore[] = [];
  public paymentTypesLegacy: PaymentTypeEnum[] = [];
  public paymentTypes: PaymentTypeEnum[] = [PaymentTypeEnum.Transfer, PaymentTypeEnum.NoPayment, PaymentTypeEnum.GiftCard];
  public paymentMethods: string[] = [];
  public releaseTypes: TicketReleaseType[] = [TicketReleaseType.Client, TicketReleaseType.Save];
  public eventStatusId: number = null;
  public eventStatuses: EventStatus[] = [];
  public eventStatusesLegacy: EventStatus[] = [];
  public transactionTypes: TransactionType[] = Object.values(TransactionType);
  public eventScrollLoad: ScrollLoad<EventLimited> = this.eventService.getScrollLoad();
  public customerScrollLoad: ScrollLoad<Customer> = this.customerService.getScrollLoad(10);
  public hasEventOwnerTransactionsRights: boolean;
  private _defaultFilters: TransactionFilter = {
    dateStart: null,
    dateEnd: null,
    transactionSalespointId: null,
    paymentType: null,
    paymentMethod: null,
    sellerUuid: null,
    transactionId: null,
    invoiceNumber: null,
    eventId: null,
    ticketId: null,
    email: null,
    transactionType: null,
    customerUuid: null,
    customerEmail: null,
    sentFromBoEmail: null,
    referenceNumber: null,
    releaseType: null,
    searchEventOwnerTransactions: false
  };
  private _defaultDatePickerConfig: DatePickerConfig = {
    range: true,
    allowDateClear: true,
    noHint: true,
    errorMessage: 'general.api_search_range_month',
    dateRanges: [DATEPICKER_PRESETS_MAPPED.Yesterday, DATEPICKER_PRESETS_MAPPED.Today, DATEPICKER_PRESETS_MAPPED.Current_week]
  };
  public dateRangeConfig: DatePickerConfig = null;
  public dateRangeConfigMobile: DatePickerConfig = null;
  private _subscriptions: Subscription[] = [];
  public isDateRangeValid: boolean;

  constructor(public authService: AuthService,
              public validationService: ValidationService,
              public customerService: CustomerService,
              public eventService: EventService,
              public userService: UserService,
              public salespointsService: SalespointsService,
              public utilService: UtilService,
              public cartService: CartService,
              public transactionsService: TransactionsService,
              private router: Router) {
  }

  public ngOnInit(): void {
    this.updateData();
    this.setupDatePicker();
    this.filters = cloneDeep(this._defaultFilters);
    this._subscriptions.push(this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.filters.eventId = null;
        this.eventScrollLoad.reset();
        setTimeout(() => {
          this.setupDatePicker();
        }, 0);
      }
    }));
    this.hasEventOwnerTransactionsRights = this.authService.checkPrivileges([Privileges.VIEW_EVENT_OWNER_TRANSACTIONS])
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  private setupDatePicker(): void {
    this._defaultDatePickerConfig.dateRanges = [DATEPICKER_PRESETS_MAPPED.Yesterday, DATEPICKER_PRESETS_MAPPED.Today, DATEPICKER_PRESETS_MAPPED.Current_week];
    this.dateRangeConfig = {
      autoBindDates: true,
      ...this._defaultDatePickerConfig
    };
    this.dateRangeConfigMobile = {
      autoBindDates: false,
      ...this._defaultDatePickerConfig
    }
  }

  public toggleFilters() {
    this.showMobileFilters = !this.showMobileFilters;
    this.showMobileFiltersChange.emit(this.showMobileFilters);
  }

  public clearFilters() {
    const filters = cloneDeep(this._defaultFilters);
    Object.keys(filters).forEach((key) => {
      (filters as any)[key] = null;
    });
    this.eventScrollLoad.reset();
    this.customerScrollLoad.reset();
    this.eventStatusId = null;
    this.sellers = [];
    this.filters = filters;
    this.noSearches = true;
  }

  public get isFormValid(): boolean {
    let check = false;
    let validNumerics = true;
    let validEmails = true;
    Object.keys(this.filters).forEach((key) => {
      if (['ticketId', 'transactionId', 'referenceNumber', 'invoiceNumber'].includes(key) && !this.validationService.emptyOrNumeric((this.filters as any)[key], true)) {
        validNumerics = false;
      } else if (['sentFromBoEmail', 'email'].includes(key) && !this.isValidEmail((this.filters as any)[key])) {
        validEmails = false;
      } else if (!['page', 'pageSize', 'sortBy', 'sortOrder'].includes(key) && (this.filters as any)[key]) {
        check = true;
      }
    });
    this.validateDateRange();
    if (check) {
      check = validNumerics && validEmails;
      if (check && this.mustDoDateCheck()) {
        check = this.isDateRangeValid;
      }
    }
    return check;
  }
  private mustDoDateCheck(): boolean {
    let dateCheck = true;
    Object.keys(this.filters).forEach((key) => {
      // Don't need to check daterange if any of these is filled
      if (['eventId', 'ticketId', 'transactionId', 'referenceNumber', 'invoiceNumber', 'customerUuid', 'customerEmail'].includes(key)) {
        if (dateCheck) {
          dateCheck = (this.filters as any)[key] == null;
        }
      }
    });
    return dateCheck;
  }

  public validateDateRange(): void {
    const dateCheck: boolean = this.mustDoDateCheck();
    if (dateCheck) {
      if (this.filters.dateStart && this.filters.dateEnd) {
        const diff: number = moment(this.filters.dateEnd).diff(moment(this.filters.dateStart), 'days');
        this.isDateRangeValid = diff <= TransactionsFiltersComponent.MAX_DATE_RANGE;
      } else {
        this.isDateRangeValid = false;
      }
    } else {
      this.isDateRangeValid = true;
    }
  }

  public updateData(spId = 0): void {
    this.updatingData = true;
    if (spId > 0) {
      this.filters.sellerUuid = null;
      this.userService.getActiveUserBySalespoint(spId).pipe(
        finalize(() => this.updatingData = false)
      ).subscribe((sellers) => this.sellers = sellers);
    } else {
      const requests: Observable<any>[] = [];
      requests.push(this.eventService.findAllEventStatuses());
      requests.push(this.salespointsService.getSalesPoints());
      requests.push(this.transactionsService.getTransactionsPaymentMethods(PaymentTypeEnum.Transfer));
      if (!this.authService.user.isNewTypeSalespoint) {
        requests.push(this.cartService.getPaymentTypes());
      }
      forkJoin(requests).pipe(
        finalize(() => {
          if (!this.filters.transactionSalespointId) {
            this.updatingData = false;
          }
        })
      ).subscribe(results => {
        this.eventStatusesLegacy = results[0].filter((item: EventStatus) => item.id !== STATUS_DRAFT);
        this.eventStatuses = results[0].filter((item: EventStatus) => !SELF_SERVICE_EXCLUDED_STATUSES.includes(item.id));
        this.salespoints = results[1] || [];
        this.paymentMethods = results[2];
        this.paymentTypesLegacy = results[3];
        this.sellers = [];
        if (this.filters.transactionSalespointId) {
          this.userService.getActiveUserBySalespoint(this.filters.transactionSalespointId).pipe(
            finalize(() => this.updatingData = false)
          ).subscribe((sellers) => this.sellers = sellers);
        }
      });
    }
  }

  displayFilterSelectedOption(id: number, list: Array<any>): string {
    return find(list, {id: id})?.name;
  }

  displayFilterUserName(id: string, list: Array<any>): string {
    const user = find(list, {userId: id});
    return user?.firstName + ' ' + user?.lastName;
  }

  public applyMobileDateChange(): void {
    if (this.isFormValid) {
      this.applySearch();
    }
  }

  public applySearch(): void {
    if (!this.filters.eventId) {
      this.eventScrollLoad.reset();
    }
    this.search.emit();
  }

  public updateCustomer(customer: Customer): void {
    this.filters.customerEmail = customer?.email || null;
  }

  public updateBuyer(customer: Customer): void {
    this.filters.buyerUuid = customer?.uuid || null;
    this.filters.email = '';
  }

  public isValidEmail(email: string): boolean {
    return !email?.length || ValidationService.validateEmail(email);
  }

  protected readonly TextInputType = TextInputType;
  protected readonly EventType = EventType;
  protected readonly PaymentTypeEnum = PaymentTypeEnum;
}
